import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useContext, useImperativeHandle, forwardRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Draggable from 'react-draggable';
import { Button, Form, ButtonGroup, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ResizableBox } from 'react-resizable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faImage, faAtom, faBold, faItalic, faUnderline, faTrash, faFont, faEye, faFileAlt, faPlus, faCopy, faPaste, faArrowsAlt, faFileExport, faCommentDots, faLink, faListUl, faListOl } from '@fortawesome/free-solid-svg-icons';
import debounce from 'lodash.debounce';
import VisualPickerModal from './VisualPickerModal';
import { UserInfo, VisualCache } from '../helper/Context';
import { generateSlideHtml } from '../helper/SlideUtils';
import 'react-resizable/css/styles.css';
import './CustomTextSlideEditor.css';
import { SketchPicker } from 'react-color';
import { APIWebVisualization } from '../helper/APIFunctions';
import { APIUploadAsset } from '../helper/APIFunctions';
import TiptapTextbox from './TiptapTextbox';

const SLIDE_BASE_WIDTH = 1280;
const SLIDE_BASE_HEIGHT = 720;
const envToggle = (process.env.REACT_APP_HIDDEN_VISUAL_FOR_DECK_IMAGES === 'true');
const getDeckHiddenVisualToggle = () => {
  try { return (localStorage.getItem('deck_hidden_visuals') === 'true') || envToggle; } catch (e) { return envToggle; }
};
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const FONT_FAMILIES = [
  'Arial', 'Verdana', 'Helvetica', 'Georgia', 'Times New Roman', 'Courier New', 'Impact', 'Comic Sans MS'
];

const getColorPickerPositioningStyle = () => {
  const isRight = document.getElementById("color-picker-button").getBoundingClientRect().left > window.innerWidth/2.;
  const style = {
    position: 'absolute',
    right: isRight ? '0px' : '',
    left: isRight ? '' : '0px'
  }
  return style;
}

const BACKGROUND_OPTIONS = [
    { name: 'White', value: '#ffffff' },
    { name: 'Light Gray', value: '#f8f9fa' },
    { name: 'Light Blue', value: '#e3f2fd' },
    { name: 'Light Green', value: '#e8f5e8' },
    { name: 'Light Pink', value: '#fce4ec' },
    { name: 'Light Yellow', value: '#fff9c4' },
    { name: 'Dark Gray', value: '#343a40' },
    { name: 'Black', value: '#000000' },
    { name: 'Blue Gradient', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { name: 'Ocean Gradient', value: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)' },
    { name: 'Sunset Gradient', value: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' },
    { name: 'Forest Gradient', value: 'linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)' },
    { name: 'Fire Gradient', value: 'linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)' },
    { name: 'Mint Gradient', value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
    { name: 'Night Gradient', value: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)' },
    { name: 'Purple Gradient', value: 'linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)' },
    { name: 'Upload Image…', value: '__upload_image__' },
];

const initialTextboxes = [
    {
      id: 1, x: 50, y: 50, width: 350, height: 'auto', text: 'This is a textbox',
      fontSize: 24,
      fontColor: '#000000',
      fontFamily: 'Arial',
      isBold: false,
      isItalic: false,
      isUnderline: false,
      linkUrl: ''
    },
];

const initialSlideState = {
    textboxes: initialTextboxes,
    images: [],
    iframes: [],
    background: '#ffffff',
    showPopup: false,
    popupTitle: '',
    popupText: '',
    popupFont: 'Arial'
};

// Memoized Textbox component to prevent unnecessary re-renders
const EditableTextbox = React.memo(({
    box,
    scale,
    isActive,
    isSelected,
    onDragStop,
    onResizeStart,
    onTextChange,
    onSetActive,
    textareaRef,
    onHtmlChange,
    onEditorReady
}) => {
    const contentWrapRef = useRef(null);
    const [measuredHeight, setMeasuredHeight] = useState(0);

    useLayoutEffect(() => {
        const el = contentWrapRef.current;
        if (!el) return;
        // Measure immediately
        const measure = () => {
            const h = Math.ceil(el.scrollHeight || el.clientHeight || 0);
            if (h && h !== measuredHeight) setMeasuredHeight(h);
        };
        measure();
        // Observe size changes
        let ro;
        try {
            ro = new ResizeObserver(() => measure());
            ro.observe(el);
        } catch (_) {
            // Fallback: poll if ResizeObserver unavailable
            const id = setInterval(measure, 250);
            return () => clearInterval(id);
        }
        return () => { try { ro && ro.disconnect(); } catch (_) {} };
    }, [measuredHeight]);

    const numericHeight = box.height === 'auto' ? Math.max(((measuredHeight || 30)) + 20, 50) : box.height;

    const bounds = {
        left: 0,
        top: 0,
        right: SLIDE_BASE_WIDTH - box.width,
        bottom: SLIDE_BASE_HEIGHT - numericHeight
    };

    return (
        <Draggable
            bounds={bounds}
            position={{ x: box.x, y: box.y }}
            onStop={(e, data) => onDragStop({id: box.id, type: 'textbox'}, e, data)}
            onDrag={(e, data) => {
                const slideX = data.x;
                const slideY = data.y;
                const clampedX = clamp(slideX, bounds.left, bounds.right);
                const clampedY = clamp(slideY, bounds.top, bounds.bottom);
                data.x = clampedX;
                data.y = clampedY;
                onDragMove({id: box.id, type: 'textbox'}, data);
            }}
            handle=".drag-handle"
            scale={scale}
            cancel={".textbox-input, .tiptap-textbox"}
            onStart={() => onSetActive({id: box.id, type: 'textbox'})}
        >
            <ResizableBox
                width={box.width}
                height={numericHeight}
                onResizeStart={(e, data) => onResizeStart(e, data, box, 'textbox')}
                resizeHandles={['w', 'e']}
                className={`textbox ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
                minConstraints={[100, 30]}
                onClick={(e) => { e.stopPropagation(); onSetActive({id: box.id, type: 'textbox'}); }}
            >
                <div>
                    <div className="drag-handle">
                        <FontAwesomeIcon icon={faArrowsAlt} className="me-2" />
                        Move
                    </div>
                    <div ref={contentWrapRef} style={{ padding: 4 }}>
                        <TiptapTextbox
                            valueText={box.text}
                            valueHtml={box.html}
                            onChangeHtml={(html) => onHtmlChange && onHtmlChange(box.id, html)}
                            onReady={(editor) => onEditorReady && onEditorReady(box.id, editor)}
                            fontSize={box.fontSize === '' ? 24 : box.fontSize}
                            fontFamily={box.fontFamily}
                            fontColor={box.fontColor}
                            isBold={box.isBold}
                            isItalic={box.isItalic}
                            isUnderline={box.isUnderline}
                        />
                    </div>
                    {/* Position indicator */}
                    {(isActive || isSelected) && (
                        <div className="position-indicator" style={{
                            position: 'absolute',
                            top: -35,
                            left: 0,
                            background: '#333',
                            color: '#fff',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: 9999,
                        }}>
                            x: {Math.round(box.x)}, y: {Math.round(box.y)}, w: {Math.round(box.width)}, h: {Math.round(numericHeight)}
                        </div>
                    )}
                    {/* Full-height resize handles */}
                    <div className="resize-handle-overlay">
                        <div className="resize-handle-w" />
                        <div className="resize-handle-e" />
                    </div>
                </div>
            </ResizableBox>
        </Draggable>
    );
});

// Update the CSS for the resize handles
const style = document.createElement('style');
style.textContent = `
    .resize-handle-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
    }
    .resize-handle-w,
    .resize-handle-e {
        position: absolute;
        width: 8px;
        height: 100%;
        top: 0;
        cursor: ew-resize;
        pointer-events: auto;
        background-color: transparent;
        transition: background-color 0.2s;
    }
    .resize-handle-w {
        left: -4px;
    }
    .resize-handle-e {
        right: -4px;
    }
    .resize-handle-w:hover,
    .resize-handle-e:hover {
        background-color: rgba(13, 110, 253, 0.2);
    }
`;
document.head.appendChild(style);

let onDragMove; // forward declaration so it can be referenced above

const EditableImage = React.memo(({
    box,
    scale,
    isActive,
    isSelected,
    onDragStop,
    onResizeStart,
    onResizeStop,
    onSetActive
}) => {
    // Calculate bounds for dragging
    const bounds = {
        left: 0,
        top: 0,
        right: SLIDE_BASE_WIDTH - box.width,
        bottom: SLIDE_BASE_HEIGHT - box.height
    };

    return (
        <Draggable
            bounds={bounds}
            position={{ x: box.x, y: box.y }}
            onStop={(e, data) => onDragStop({id: box.id, type: 'image'}, e, data)}
            onDrag={(e, data) => {
                // Convert screen coordinates to slide coordinates
                const slideX = data.x;
                const slideY = data.y;
                
                // Apply bounds checking in slide coordinates
                const clampedX = clamp(slideX, bounds.left, bounds.right);
                const clampedY = clamp(slideY, bounds.top, bounds.bottom);
                
                // Update position with clamped coordinates
                data.x = clampedX;
                data.y = clampedY;
                
                onDragMove({id: box.id, type: 'image'}, data);
            }}
            handle=".drag-handle"
            scale={scale}
            onStart={() => onSetActive({id: box.id, type: 'image'})}
        >
            <ResizableBox
                width={box.width}
                height={box.height}
                onResizeStart={(e, data) => onResizeStart(e, data, box, 'image')}
                onResizeStop={onResizeStop}
                resizeHandles={['se', 'sw', 'ne', 'nw', 'n', 's', 'e', 'w']}
                className={`imagebox ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
                minConstraints={[50, 50]}
                maxConstraints={[SLIDE_BASE_WIDTH, SLIDE_BASE_HEIGHT]}
                onClick={(e) => { e.stopPropagation(); onSetActive({id: box.id, type: 'image'}); }}
                lockAspectRatio={true}
                axis="both"
            >
                <div>
                    <div className="drag-handle">
                        <FontAwesomeIcon icon={faArrowsAlt} className="me-2" />
                        Move
                    </div>
                    <img 
                        src={box.src} 
                        alt="slide element" 
                        className="image-element" 
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                    />
                    {/* Position indicator */}
                    {(isActive || isSelected) && (
                        <div className="position-indicator" style={{
                            position: 'absolute',
                            top: -35,
                            left: 0,
                            background: '#333',
                            color: '#fff',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: 9999,
                        }}>
                            x: {Math.round(box.x)}, y: {Math.round(box.y)}, w: {Math.round(box.width)}, h: {Math.round(box.height)}
                        </div>
                    )}
                </div>
            </ResizableBox>
        </Draggable>
    );
});

// Get QR URL from visual cache or generate it
const getQrUrl = (src, visCache) => {
  if (!src?.includes('vis.snorkle')) return null;
  
  // Extract visual ID from URL - try multiple patterns
  let id = null;
  
  // Try different URL patterns
  const patterns = [
    /vis\.snorkle\.io\/([^/?#]+)\.html/,
    /vis\.snorkle\.io\/([^/?#]+)\/viewer\.html/,
    /vis\.snorkle\.io\/([^/?#]+)/,
    /vis\.snorkle\.io.*[?&]id=([^&]+)/
  ];
  
  for (const pattern of patterns) {
    const match = src.match(pattern);
    if (match) {
      // Get the raw ID and clean it up
      id = match[1];
      // Remove trailing slash if present (from /viewer.html URLs)
      id = id.replace(/\/$/, '');
      break;
    }
  }
  
  if (!id) return null;
  
  const visual = visCache?.[id];
  if (!visual) return null;


  // First try to get QR from cache
  if (visual.qr_image || visual.qr_url) {
    const qrUrl = visual.qr_image || visual.qr_url;
    return qrUrl;
  }

  // If no cached QR, use the same domain as the visual URL
  // This matches how QRs are stored for sketchfab visuals
  const qrUrl = `${src.replace(/\.html$/, '')}_qr.jpg`;
  return qrUrl;
};

// Update the EditableIframe component
const EditableIframe = React.memo(({
    box,
    scale,
    isActive,
    isSelected,
    onDragStop,
    onResizeStart,
    onResizeStop,
  onSetActive,
  slideState
}) => {
  const { visCache } = useContext(VisualCache);
  const isSnorkleVisual = box.src?.includes('vis.snorkle');
  const showQR = isSnorkleVisual && slideState.iframeSettings?.[box.id]?.showQR;
  
  // Only compute QR URL when needed
  const qrUrl = useMemo(() => {
    if (!isSnorkleVisual || !showQR) return null;
    return getQrUrl(box.src, visCache);
  }, [isSnorkleVisual, showQR, box.src, visCache]);

  // Log only when a Snorkle visual is first selected
  useEffect(() => {
    if (isActive && isSnorkleVisual) {
    }
  }, [isActive]); // Only run when active state changes

  // Calculate bounds for dragging
  const bounds = {
    left: 0,
    top: 0,
    right: SLIDE_BASE_WIDTH - box.width,
    bottom: SLIDE_BASE_HEIGHT - box.height
  };

    return (
        <Draggable
      bounds={bounds}
            position={{ x: box.x, y: box.y }}
            onStop={(e, data) => onDragStop({id: box.id, type: 'iframe'}, e, data)}
            handle=".drag-handle"
            scale={scale}
            onStart={() => onSetActive({id: box.id, type: 'iframe'})}
        >
            <ResizableBox
                width={box.width}
                height={box.height}
                onResizeStart={(e, data) => onResizeStart(e, data, box, 'iframe')}
                onResizeStop={onResizeStop}
                className={`iframebox ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
        minConstraints={[200, 150]}
        maxConstraints={[SLIDE_BASE_WIDTH, SLIDE_BASE_HEIGHT]}
        onClick={(e) => {
          e.stopPropagation();
          onSetActive({id: box.id, type: 'iframe'});
        }}
            >
                <div>
          <div 
            className="drag-handle"
            onClick={(e) => {
              e.stopPropagation();
              onSetActive({id: box.id, type: 'iframe'});
            }}
          >
            <FontAwesomeIcon icon={faArrowsAlt} className="me-2" />
            Move
          </div>
                    <iframe
                        src={box.src}
            title={`iframe-${box.id}`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              pointerEvents: isActive ? 'auto' : 'none'
            }}
          />
          {showQR && box.qrImage && (
            <div className="qr-code-overlay">
              <img 
                src={box.qrImage} 
                alt="QR Code"
                className="qr-code"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
                </div>
            </ResizableBox>
        </Draggable>
    );
});

// Add new type definitions for slide formats
const SLIDE_FORMATS = {
  LEGACY: 'legacy',
  MODERN: 'modern'
};

// Function to determine if a slide deck is legacy format
const isLegacyDeck = (slides) => {
  // Legacy decks have slides that are just URLs with metadata
  return slides.every(slide => typeof slide === 'object' && slide.url && !slide.elements);
};

// Function to convert slide state to exportable JSON
const generateSlideJSON = (slideState) => {
  const {
    textboxes,
    images,
    iframes,
    background,
    showPopup,
    popupTitle,
    popupText
  } = slideState;

  return {
    version: '1.0',
    format: SLIDE_FORMATS.MODERN,
    popup: showPopup ? {
      title: popupTitle,
      text: popupText
    } : null,
    elements: {
      textboxes: textboxes.map(box => ({
        id: box.id,
        type: 'text',
        content: box.text,
        html: box.html || null,
        position: {
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height
        },
        style: {
          fontSize: box.fontSize,
          fontColor: box.fontColor,
          fontFamily: box.fontFamily,
          isBold: box.isBold,
          isItalic: box.isItalic,
          isUnderline: box.isUnderline
        }
      })),
      images: images.map(img => ({
        id: img.id,
        type: 'image',
        src: img.src,
        position: {
          x: img.x,
          y: img.y,
          width: img.width,
          height: img.height
        },
        aspectRatio: img.aspectRatio
      })),
      iframes: iframes.map(frame => ({
        id: frame.id,
        type: 'iframe',
        src: frame.src,
        position: {
          x: frame.x,
          y: frame.y,
          width: frame.width,
          height: frame.height
        }
      }))
    },
    background: background
  };
};

// Function to export deck as JSON file
const exportDeckAsJSON = (slideState) => {
  const jsonData = generateSlideJSON(slideState);
  const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'slide_deck.json';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Convert to forwardRef to allow parent to access methods
const CustomTextSlideEditor = forwardRef(({ 
  slideState: initialSlideState, 
  onSlideStateChange, 
  onOpenVisualPicker, 
  onIframeSelect,
  onSave, 
  onCancel,
  embedded = false,
  isThumbnail = false
}, ref) => {
  const { visCache } = useContext(VisualCache);
  const [slideState, setSlideState] = useState(initialSlideState || {
    textboxes: [
      {
        id: 1, x: 50, y: 50, width: 400, height: 'auto', text: 'Title',
        fontSize: 48, fontFamily: 'Arial', fontColor: '#000000', isBold: true, isItalic: false, isUnderline: false,
      },
      {
        id: 2, x: 50, y: 150, width: 400, height: 'auto', text: 'Subtitle',
        fontSize: 24, fontFamily: 'Arial', fontColor: '#333333', isBold: false, isItalic: false, isUnderline: false,
      },
    ],
    images: [],
    iframes: [],
    background: '#ffffff',
    showPopup: false,
    popupTitle: '',
    popupText: '',
    popupFont: 'Arial'
  });

  const [activeElement, setActiveElement] = useState(null); // {id, type}
  const [scale, setScale] = useState(1);
  const [fontSizeInput, setFontSizeInput] = useState(''); // Local state for font size input
  const fontSizeInputFocused = useRef(false); // Track if font size input is being edited
  const slideCanvasRef = useRef(null);
  const textareaRefs = useRef({});
  const imageInputRef = useRef(null);
  const slideStateRef = useRef(slideState);
  slideStateRef.current = slideState;
  const resizeStartData = useRef(null);
  const isResizing = useRef(false);
  const lastPropSlideStateRef = useRef(null);
  const resizeRafRef = useRef(null);
  const dragRafRef = useRef(null);
  
  const { userInfo } = useContext(UserInfo);

  // Only use internal visual picker when onOpenVisualPicker is not provided
  const [showVisualPicker, setShowVisualPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Selection box state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [selectedElements, setSelectedElements] = useState([]);

  // Text editing state
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const isUndoRedoRef = useRef(false);

  // Copy/paste state
  const [copiedElements, setCopiedElements] = useState([]);

  // Undo/Redo state - only for non-embedded mode
  const [history, setHistory] = useState([initialSlideState || {
    textboxes: [
      {
        id: 1, x: 50, y: 50, width: 400, height: 'auto', text: 'Title',
        fontSize: 48, fontFamily: 'Arial', fontColor: '#000000', isBold: true, isItalic: false, isUnderline: false,
      },
      {
        id: 2, x: 50, y: 150, width: 400, height: 'auto', text: 'Subtitle',
        fontSize: 24, fontFamily: 'Arial', fontColor: '#333333', isBold: false, isItalic: false, isUnderline: false,
      },
    ],
    images: [],
    iframes: [],
    background: '#ffffff',
    showPopup: false,
    popupTitle: '',
    popupText: '',
    popupFont: 'Arial'
  }]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Add a ref to track if we're handling a delete
  const isHandlingDelete = useRef(false);

  // Function to check if an element is within the selection box
  const isElementInSelection = useCallback((element, selectionBox) => {
    const { left, top, right, bottom } = selectionBox;
    const elementRight = element.x + element.width;
    const elementBottom = element.y + (element.height === 'auto' ? 50 : element.height);
    
    // Check if element overlaps with selection box
    return !(element.x > right || 
             elementRight < left || 
             element.y > bottom || 
             elementBottom < top);
  }, []);

  // Function to get elements within selection
  const getElementsInSelection = useCallback((start, end) => {
    if (!start || !end) return [];
    
    const selectionBox = {
      left: Math.min(start.x, end.x),
      top: Math.min(start.y, end.y),
      right: Math.max(start.x, end.x),
      bottom: Math.max(start.y, end.y)
    };
    
    const selectedElements = [];
    
    // Check textboxes
    slideState.textboxes.forEach(box => {
      if (isElementInSelection(box, selectionBox)) {
        selectedElements.push({ id: box.id, type: 'textbox' });
      }
    });
    
    // Check images
    slideState.images.forEach(box => {
      if (isElementInSelection(box, selectionBox)) {
        selectedElements.push({ id: box.id, type: 'image' });
      }
    });
    
    // Check iframes
    slideState.iframes.forEach(box => {
      if (isElementInSelection(box, selectionBox)) {
        selectedElements.push({ id: box.id, type: 'iframe' });
      }
    });
    
    return selectedElements;
  }, [slideState.textboxes, slideState.images, slideState.iframes, isElementInSelection]);

  // Function to check if an element is selected
  const isElementSelected = useCallback((id, type) => {
    return selectedElements.some(el => el.id === id && el.type === type);
  }, [selectedElements]);

  // Update internal state when prop changes
  useEffect(() => {
    if (initialSlideState && JSON.stringify(initialSlideState) !== JSON.stringify(lastPropSlideStateRef.current)) {
      lastPropSlideStateRef.current = initialSlideState;
      setSlideState(initialSlideState);
      
      // Only manage history in non-embedded mode
      if (!embedded) {
        // Add to history only if it's truly different from the current history state
        setHistory(prev => {
          const currentState = prev[prev.length - 1];
          if (!currentState || JSON.stringify(initialSlideState) !== JSON.stringify(currentState)) {
            const newHistory = [...prev, initialSlideState];
            setHistoryIndex(newHistory.length - 1);
            return newHistory;
          }
          return prev;
        });
      }
    }
  }, [embedded, initialSlideState]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showColorPicker && !e.target.closest('.color-picker-wrapper')) {
        setShowColorPicker(false);
      }
    };
    
    if (showColorPicker) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showColorPicker]);

  const recordHistory = useCallback((newSlideState, updateStateImmediately = true) => {
    // Always maintain history for undo/redo functionality
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newSlideState);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);

    // Keep ref in sync immediately so preview/export reads latest
    slideStateRef.current = newSlideState;
    
    // Update local state if requested
    if (updateStateImmediately) {
      setSlideState(newSlideState);
      
      // In embedded mode, notify parent
      if (embedded && onSlideStateChange) {
        onSlideStateChange(newSlideState);
      }
    }
  }, [embedded, onSlideStateChange, history, historyIndex]);

  useEffect(() => {
    const handleUndoRedo = (e) => {
        if (e.ctrlKey || e.metaKey) {
            if (e.key === 'z') {
                e.preventDefault();
                if (e.shiftKey) { // Redo
                    if (historyIndex < history.length - 1) {
                        isUndoRedoRef.current = true;
                        setHistoryIndex(prev => prev + 1);
                    }
                } else { // Undo
                    if (historyIndex > 0) {
                        isUndoRedoRef.current = true;
                        setHistoryIndex(prev => prev - 1);
                    }
                }
            } else if (e.key === 'y') { // Redo
                e.preventDefault();
                if (historyIndex < history.length - 1) {
                    isUndoRedoRef.current = true;
                    setHistoryIndex(prev => prev + 1);
                }
            }
        }
    };
    window.addEventListener('keydown', handleUndoRedo);
    return () => window.removeEventListener('keydown', handleUndoRedo);
  }, [history.length, historyIndex]);

  // Copy selected elements
  const handleCopy = useCallback(() => {
    if (!activeElement && selectedElements.length === 0) return;
    
    const elementsToCopy = [];
    
    // Copy active element or selected elements
    const elementsToProcess = selectedElements.length > 0 ? selectedElements : [activeElement];
    
    elementsToProcess.forEach(element => {
      if (element.type === 'textbox') {
        const textbox = slideStateRef.current.textboxes.find(box => box.id === element.id);
        if (textbox) elementsToCopy.push({ ...textbox, type: 'textbox' });
      } else if (element.type === 'image') {
        const image = slideStateRef.current.images.find(img => img.id === element.id);
        if (image) elementsToCopy.push({ ...image, type: 'image' });
      } else if (element.type === 'iframe') {
        const iframe = slideStateRef.current.iframes.find(ifr => ifr.id === element.id);
        if (iframe) elementsToCopy.push({ ...iframe, type: 'iframe' });
      }
    });
    
    if (elementsToCopy.length > 0) {
      setCopiedElements(elementsToCopy);
      // Could add a toast notification here in the future
    }
  }, [activeElement, selectedElements]);

  // Paste copied elements
  const handlePaste = useCallback(() => {
    if (copiedElements.length === 0) return;
    
    const PASTE_OFFSET = 20; // Offset for pasted elements
    let newSlideState = { ...slideStateRef.current };
    const pastedElementIds = [];
    
    copiedElements.forEach(element => {
      if (element.type === 'textbox') {
        const newId = newSlideState.textboxes.length > 0 ? Math.max(...newSlideState.textboxes.map(t => t.id)) + 1 : 1;
        const newTextbox = {
          ...element,
          id: newId,
          x: Math.min(element.x + PASTE_OFFSET, 1200), // Keep within bounds
          y: Math.min(element.y + PASTE_OFFSET, 600),   // Keep within bounds
          stackOrder: Date.now()
        };
        delete newTextbox.type; // Remove the type field we added for copying
        newSlideState.textboxes = [...newSlideState.textboxes, newTextbox];
        pastedElementIds.push({ id: newId, type: 'textbox' });
      } else if (element.type === 'image') {
        const newId = newSlideState.images.length > 0 ? Math.max(...newSlideState.images.map(i => i.id)) + 1 : 1;
        const newImage = {
          ...element,
          id: newId,
          x: Math.min(element.x + PASTE_OFFSET, 1200), // Keep within bounds
          y: Math.min(element.y + PASTE_OFFSET, 600),   // Keep within bounds
          stackOrder: Date.now()
        };
        delete newImage.type; // Remove the type field we added for copying
        newSlideState.images = [...newSlideState.images, newImage];
        pastedElementIds.push({ id: newId, type: 'image' });
      } else if (element.type === 'iframe') {
        const newId = newSlideState.iframes.length > 0 ? Math.max(...newSlideState.iframes.map(i => i.id)) + 1 : 1;
        const newIframe = {
          ...element,
          id: newId,
          x: Math.min(element.x + PASTE_OFFSET, 1200), // Keep within bounds
          y: Math.min(element.y + PASTE_OFFSET, 600),   // Keep within bounds
          stackOrder: Date.now()
        };
        delete newIframe.type; // Remove the type field we added for copying
        newSlideState.iframes = [...newSlideState.iframes, newIframe];
        pastedElementIds.push({ id: newId, type: 'iframe' });
      }
    });
    
    recordHistory(newSlideState);
    
    // Select the pasted elements
    if (pastedElementIds.length === 1) {
      setActiveElement(pastedElementIds[0]);
      setSelectedElements([]);
    } else if (pastedElementIds.length > 1) {
      setSelectedElements(pastedElementIds);
      setActiveElement(null);
    }
    
  }, [copiedElements, recordHistory]);

  // Copy/Paste keyboard shortcuts
  useEffect(() => {
    const handleCopyPaste = (e) => {
      // Don't handle copy/paste if user is typing/editing text
      const activeElement = document.activeElement;
      const isEditingText = (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable ||
        activeElement.classList?.contains('ProseMirror') ||
        activeElement.closest?.('.ProseMirror') ||
        activeElement.closest?.('.tiptap-textbox')
      );
      
      if (!isEditingText && (e.ctrlKey || e.metaKey)) {
        if (e.key === 'c') {
          e.preventDefault();
          handleCopy();
        } else if (e.key === 'v') {
          e.preventDefault();
          handlePaste();
        }
      }
    };
    
    window.addEventListener('keydown', handleCopyPaste);
    return () => window.removeEventListener('keydown', handleCopyPaste);
  }, [handleCopy, handlePaste]);

  // Syncs parent slide state down to the editor's state.
  useEffect(() => {
    if (isResizing.current) return; // Don't sync while actively resizing

    // A guard to prevent an update loop. If the incoming props are the same as the
    // last state we sent to the parent (which is the current history state),
    // we don't need to update our internal state again.
    const lastSentState = history[historyIndex];
    if (initialSlideState && JSON.stringify(initialSlideState) !== JSON.stringify(lastSentState)) {
        setSlideState(initialSlideState);
        // Keep ref in sync
        slideStateRef.current = initialSlideState;
    }
  }, [initialSlideState, history, historyIndex]);

  // Handles undo/redo history changes and notifies the parent.
  useEffect(() => {
    if (!isUndoRedoRef.current) return; // Only react to explicit undo/redo
    const currentHistoryState = history[historyIndex];
    if (currentHistoryState) {
      setSlideState(currentHistoryState); // Update internal state from history
      // Keep ref in sync
      slideStateRef.current = currentHistoryState;
      if (embedded && onSlideStateChange) {
        onSlideStateChange(currentHistoryState); // Notify parent of the change
      }
    }
    isUndoRedoRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyIndex]);

  // Auto-resize function that updates both DOM and React state
  const autoResizeTextarea = useCallback((textarea, textboxId) => {
    if (textarea) {
      // Store current scroll position to prevent jumping
      const originalScrollTop = textarea.scrollTop;
      
      // Reset height to auto to get the natural height
      textarea.style.height = 'auto';
      
      // Set a minimum height to prevent collapsing
      const minHeight = 30;
      const DRAG_HANDLE_HEIGHT = 20;
      const textareaHeight = Math.max(textarea.scrollHeight, minHeight);
      const totalHeight = textareaHeight + DRAG_HANDLE_HEIGHT;
      
      // Apply the calculated height to DOM
      textarea.style.height = textareaHeight + 'px';
      
      // Update React state to match the new height, but only if it has changed
      if (textboxId && embedded) {
        const currentBox = slideStateRef.current.textboxes.find(b => b.id === textboxId);
        if (currentBox && currentBox.height !== totalHeight) {
          const newTextboxes = slideStateRef.current.textboxes.map(box =>
            box.id === textboxId ? { ...box, height: totalHeight } : box
          );
          const newSlideState = { ...slideStateRef.current, textboxes: newTextboxes };
          setSlideState(newSlideState);
          
          // Notify parent in embedded mode
          if (onSlideStateChange) {
            onSlideStateChange(newSlideState);
          }
        }
      }
      
      // Restore scroll position
      textarea.scrollTop = originalScrollTop;
    }
  }, [embedded, onSlideStateChange]);

  // This effect recalculates the height of textboxes when their height is set to 'auto'.
  // It runs after the DOM has been updated but before the browser paints, preventing flicker.
  useLayoutEffect(() => {
    slideState.textboxes?.forEach(box => {
      // Only trigger resize for textboxes that have been marked for it.
      if (box.height === 'auto') {
        const textarea = textareaRefs.current[box.id];
        if (textarea) {
          autoResizeTextarea(textarea, box.id);
        }
      }
    });
  }, [slideState.textboxes, autoResizeTextarea]);

  // Update the scaling effect to use a consistent scaling approach
  useEffect(() => {
    const handleResize = () => {
      if (slideCanvasRef.current) {
        const { width, height } = slideCanvasRef.current.getBoundingClientRect();
        const scaleX = width / SLIDE_BASE_WIDTH;
        const scaleY = height / SLIDE_BASE_HEIGHT;
        const newScale = Math.min(scaleX, scaleY);
        setScale(newScale);
        
        // Apply scale to root for consistent scaling across editor and preview
        document.documentElement.style.setProperty('--scale', newScale.toString());
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial scale
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Remove the problematic auto-resize effect that causes infinite loops
  // Height adjustments will be handled only during text changes

  const onResizeStart = useCallback((e, data, box, type) => {
    e.stopPropagation();
    isResizing.current = true;
    document.body.style.cursor = data.handle === 'w' ? 'w-resize' : 'e-resize';

    // Get the editor canvas element and its bounds
    const editorCanvas = slideCanvasRef.current;
    const canvasBounds = editorCanvas.getBoundingClientRect();
    
    resizeStartData.current = {
      id: box.id,
      type,
      initialWidth: box.width,
      initialHeight: box.height,
      initialX: box.x,
        initialY: box.y,
      initialClientX: e.clientX,
      initialClientY: e.clientY,
      handle: data.handle,
        canvasBounds, // Store canvas bounds for resize calculations
    };
    
    window.addEventListener('mousemove', onResize);
    window.addEventListener('mouseup', onResizeStop);
  }, []);

  const onResize = useCallback((e) => {
    if (!resizeStartData.current) return;
    
    const { 
        id, 
        type, 
        initialWidth, 
        initialHeight, 
        initialX, 
        initialY, 
        initialClientX, 
        initialClientY, 
        handle,
        canvasBounds 
    } = resizeStartData.current;
    
    // Calculate delta relative to the editor canvas
    const currentXInCanvas = e.clientX - canvasBounds.left;
    const initialXInCanvas = initialClientX - canvasBounds.left;
    const currentYInCanvas = e.clientY - canvasBounds.top;
    const initialYInCanvas = initialClientY - canvasBounds.top;

    // Calculate deltas in canvas coordinates
    const dx = ((currentXInCanvas - initialXInCanvas) / canvasBounds.width) * SLIDE_BASE_WIDTH;
    const dy = ((currentYInCanvas - initialYInCanvas) / canvasBounds.height) * SLIDE_BASE_HEIGHT;

    let newWidth = initialWidth;
    let newHeight = initialHeight;
    let newX = initialX;
    let newY = initialY;

    // Find the current element
    const element = type === 'image' 
        ? slideStateRef.current.images.find(img => img.id === id)
        : type === 'iframe' 
            ? slideStateRef.current.iframes.find(ifr => ifr.id === id)
            : slideStateRef.current.textboxes.find(txt => txt.id === id);

    if (!element) return;

    // Handle resizing with aspect ratio for images
    if (type === 'image') {
        const aspectRatio = element.aspectRatio || initialWidth / initialHeight;
        
        if (handle.includes('e') || handle.includes('w')) {
            // Horizontal resize
            newWidth = Math.max(50, initialWidth + (handle.includes('e') ? dx : -dx));
            newHeight = newWidth / aspectRatio;
        } else if (handle.includes('n') || handle.includes('s')) {
            // Vertical resize
            newHeight = Math.max(50, initialHeight + (handle.includes('s') ? dy : -dy));
            newWidth = newHeight * aspectRatio;
        }

        // Handle corner resize
        if (handle.includes('n') && handle.includes('w')) {
            newWidth = Math.max(50, initialWidth - dx);
            newHeight = newWidth / aspectRatio;
            newX = initialX - (newWidth - initialWidth);
            newY = initialY - (newHeight - initialHeight);
        } else if (handle.includes('n') && handle.includes('e')) {
            newWidth = Math.max(50, initialWidth + dx);
            newHeight = newWidth / aspectRatio;
            newY = initialY - (newHeight - initialHeight);
        } else if (handle.includes('s') && handle.includes('w')) {
            newWidth = Math.max(50, initialWidth - dx);
            newHeight = newWidth / aspectRatio;
            newX = initialX - (newWidth - initialWidth);
        } else if (handle.includes('s') && handle.includes('e')) {
            newWidth = Math.max(50, initialWidth + dx);
            newHeight = newWidth / aspectRatio;
        }
    } else {
        // Handle text and iframe resizing
    if (handle.includes('e')) {
            newWidth = Math.max(100, initialWidth + dx);
    } else if (handle.includes('w')) {
            const proposedWidth = Math.max(100, initialWidth - dx);
            const deltaWidth = proposedWidth - initialWidth;
            newWidth = proposedWidth;
            newX = initialX - deltaWidth;
    }

        if ((type === 'iframe') && !handle.includes('w')) {
        if (handle.includes('s')) {
                newHeight = Math.max(50, initialHeight + dy);
        } else if (handle.includes('n')) {
                const proposedHeight = Math.max(50, initialHeight - dy);
                const deltaHeight = proposedHeight - initialHeight;
                newHeight = proposedHeight;
                newY = initialY - deltaHeight;
            }
        }
    }

    // Ensure we don't exceed canvas bounds
    newWidth = Math.min(newWidth, SLIDE_BASE_WIDTH - newX);
    if (type !== 'textbox') {
        newHeight = Math.min(newHeight, SLIDE_BASE_HEIGHT - newY);
    }

    let newSlideState = { ...slideStateRef.current };

    // Update state based on element type
    if (type === 'textbox') {
        const newTextboxes = slideStateRef.current.textboxes.map(b =>
            b.id === id ? { ...b, width: newWidth, x: newX } : b
        );
        newSlideState = { ...newSlideState, textboxes: newTextboxes };
    } else if (type === 'image') {
        const newImages = slideStateRef.current.images.map(b =>
            b.id === id ? { ...b, width: newWidth, height: newHeight, x: newX, y: newY } : b
        );
        newSlideState = { ...newSlideState, images: newImages };
    } else if (type === 'iframe') {
        const newIframes = slideStateRef.current.iframes.map(b =>
            b.id === id ? { ...b, width: newWidth, height: newHeight, x: newX, y: newY } : b
        );
        newSlideState = { ...newSlideState, iframes: newIframes };
    }

    // Update ref immediately and schedule state update
    slideStateRef.current = newSlideState;
    if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
    }
    resizeRafRef.current = requestAnimationFrame(() => {
        setSlideState(newSlideState);
        
        // Update parent in embedded mode
        if (embedded && onSlideStateChange) {
            onSlideStateChange(newSlideState);
        }
    });
}, [embedded, onSlideStateChange]);

  const onResizeStop = useCallback(() => {
    if (resizeStartData.current) {
        const { id, type } = resizeStartData.current;
        let finalState = { ...slideStateRef.current };

        if (type === 'textbox') {
            const newTextboxes = slideStateRef.current.textboxes.map(b =>
                b.id === id ? { ...b, height: 'auto' } : b
            );
            finalState = { ...finalState, textboxes: newTextboxes };
        }
        recordHistory(finalState);
    }
    isResizing.current = false;
    document.body.style.cursor = 'auto';
    resizeStartData.current = null;
    if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
    window.removeEventListener('mousemove', onResize);
    window.removeEventListener('mouseup', onResizeStop);
  }, [recordHistory, onResize]);
  
  const handleAddTextbox = () => {
    const newId = slideStateRef.current.textboxes.length > 0 ? Math.max(...slideStateRef.current.textboxes.map(t => t.id)) + 1 : 1;
    const newTextbox = {
      id: newId, x: 10, y: 10, width: 200, height: 'auto', text: 'New Textbox',
      fontSize: 24,
      fontColor: '#000000',
      fontFamily: 'Arial',
      isBold: false,
      isItalic: false,
      isUnderline: false,
      stackOrder: Date.now()
    };
    const newTextboxes = [...slideStateRef.current.textboxes, newTextbox];
    recordHistory({ ...slideStateRef.current, textboxes: newTextboxes });
    setActiveElement({id: newId, type: 'textbox'});
  };
  
  const handleImageUpload = useCallback((e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
              const newId = (slideStateRef.current.images.length > 0 ? Math.max(...slideStateRef.current.images.map(i => i.id)) : 0) + 1;
            
            // Calculate initial dimensions while maintaining aspect ratio
            const maxWidth = SLIDE_BASE_WIDTH * 0.8; // 80% of slide width
            const maxHeight = SLIDE_BASE_HEIGHT * 0.8; // 80% of slide height
            const aspectRatio = img.naturalWidth / img.naturalHeight;
            
            let width = maxWidth;
            let height = width / aspectRatio;
            
            if (height > maxHeight) {
                height = maxHeight;
                width = height * aspectRatio;
            }
            
            // Center the image on the slide
            const x = (SLIDE_BASE_WIDTH - width) / 2;
            const y = (SLIDE_BASE_HEIGHT - height) / 2;

            // Use a preview data URL immediately, then upload and swap to final URL
            const previewSrc = file.path || event.target.result;
            
              const newImage = {
                  id: newId,
                src: previewSrc,
                x,
                y,
                width,
                height,
                aspectRatio,
                originalFile: file, // Store the original file for server upload
                stackOrder: Date.now()
              };
              const newImages = [...slideStateRef.current.images, newImage];
              recordHistory({ ...slideStateRef.current, images: newImages });
              setActiveElement({id: newId, type: 'image'});

              // Upload to assets bucket and replace preview with final URL
              APIUploadAsset(
                file,
                'deck_image',
                ({ finalUrl }) => {
                  try {
                  } catch (e) {}
                  if (!finalUrl) return;
                  let updatedImages = slideStateRef.current.images.map((imgBox) =>
                    imgBox.id === newId ? { ...imgBox, src: finalUrl, originalFile: undefined } : imgBox
                  );
                  recordHistory({ ...slideStateRef.current, images: updatedImages });
                },
                (err) => {
                  try {
                  } catch (e) {}
                }
              );
          };
          img.src = event.target.result;
      };
      reader.readAsDataURL(file);
      // Reset file input value to allow uploading the same file again
      e.target.value = null;
  }, [recordHistory]);

  // Use a temporary input to robustly open the file picker (avoids ref quirks)
  const openImageFilePicker = useCallback(() => {
    const tempInput = document.createElement('input');
    tempInput.type = 'file';
    tempInput.accept = 'image/*';
    tempInput.style.position = 'fixed';
    tempInput.style.left = '-9999px';
    tempInput.onchange = (evt) => {
      try {
        handleImageUpload(evt);
      } finally {
        if (tempInput && tempInput.parentNode) {
          tempInput.parentNode.removeChild(tempInput);
        }
      }
    };
    document.body.appendChild(tempInput);
    tempInput.click();
  }, [handleImageUpload]);

  const handleVisualSelected = (visual) => {
    const newId = `iframe-${Date.now()}`;

    const newIframe = {
        id: newId,
      type: 'iframe',
        src: visual.visualization_url,
      x: 50,
      y: 50,
      width: Math.min(SLIDE_BASE_WIDTH - 100, 640),
      height: Math.min(SLIDE_BASE_HEIGHT - 100, 360),
      stackOrder: Date.now(),
      qrImage: visual.qr_image,
    };

    const newState = {
      ...slideState,
      iframes: [...slideState.iframes, newIframe],
      iframeSettings: {
        ...slideState.iframeSettings,
        [newId]: {
          showQR: false  // Initialize QR as hidden for new visuals
        }
      }
    };


    onSlideStateChange(newState);
    setShowVisualPicker(false);
  };

  // Update the debouncedRecordHistory to use a shorter delay
  const debouncedRecordHistory = useCallback(
    debounce((newState) => {
    recordHistory(newState, false);
    if (!embedded) {
      lastPropSlideStateRef.current = newState;
    }
        setIsTyping(false);
    }, 300), // Reduced from 1000ms to 300ms
    [recordHistory, embedded]
);
    
  // Update the handleTextChange function to be more performant
  const handleTextChange = useCallback((id, newText) => {
      // Immediately update the UI without waiting for debounce
    const newTextboxes = slideStateRef.current.textboxes.map(box =>
      box.id === id ? { ...box, text: newText, height: 'auto' } : box
    );
    const newSlideState = { ...slideStateRef.current, textboxes: newTextboxes };

      // Update ref and state immediately for responsive UI
    slideStateRef.current = newSlideState;
    setSlideState(newSlideState);

      // Notify parent in embedded mode
    if (embedded && onSlideStateChange) {
      onSlideStateChange(newSlideState);
    }

      // Schedule auto-resize on next frame
      requestAnimationFrame(() => {
      const textarea = textareaRefs.current[id];
          if (textarea) {
              // Store current scroll position
              const scrollTop = textarea.scrollTop;
              
              // Reset height to auto and get scrollHeight
              textarea.style.height = 'auto';
              const scrollHeight = textarea.scrollHeight;
              
              // Set new height
              const newHeight = Math.max(scrollHeight, 30);
              textarea.style.height = `${newHeight}px`;
              
              // Restore scroll position
              textarea.scrollTop = scrollTop;
          }
      });

      // Debounce history recording to avoid performance issues
    debouncedRecordHistory(newSlideState);
  }, [embedded, onSlideStateChange, debouncedRecordHistory]);

  onDragMove = useCallback((element, data) => {
    const { id, type } = element;
    const box = slideStateRef.current[type === 'textbox' ? 'textboxes' : type === 'image' ? 'images' : 'iframes'].find(b => b.id === id);
    if (!box) return;

    // Calculate bounds based on current scale
    const bounds = {
        left: 0,
        top: 0,
        right: SLIDE_BASE_WIDTH - box.width,
        bottom: SLIDE_BASE_HEIGHT - (box.height === 'auto' ? 50 : box.height)
    };

    // Apply bounds checking
    const clampedX = clamp(data.x, bounds.left, bounds.right);
    const clampedY = clamp(data.y, bounds.top, bounds.bottom);

    // Create new state with updated position
    let newSlideState = { ...slideStateRef.current };
    const updatePosition = (items, itemType) => items.map(item => 
        item.id === id ? { ...item, x: clampedX, y: clampedY } : item
    );

    if (type === 'textbox') {
        newSlideState.textboxes = updatePosition(newSlideState.textboxes);
    } else if (type === 'image') {
        newSlideState.images = updatePosition(newSlideState.images);
    } else if (type === 'iframe') {
        newSlideState.iframes = updatePosition(newSlideState.iframes);
    }

    // Update ref immediately
    slideStateRef.current = newSlideState;

    // Use RAF to avoid state updates during drag
    if (dragRafRef.current) {
        cancelAnimationFrame(dragRafRef.current);
    }
    dragRafRef.current = requestAnimationFrame(() => {
        setSlideState(newSlideState);
        
        // Update parent in embedded mode
        if (embedded && onSlideStateChange) {
            onSlideStateChange(newSlideState);
        }
    });
}, [embedded, onSlideStateChange]);

  const onDragStop = useCallback((element, e, data) => {
    const { id, type } = element;
    
    // Calculate bounds
    const box = slideStateRef.current[type === 'textbox' ? 'textboxes' : type === 'image' ? 'images' : 'iframes'].find(b => b.id === id);
    if (!box) return;

    const bounds = {
        left: 0,
        top: 0,
        right: SLIDE_BASE_WIDTH - box.width,
        bottom: SLIDE_BASE_HEIGHT - (box.height === 'auto' ? 50 : box.height)
    };

    // Apply bounds checking one final time
    const clampedX = clamp(data.x, bounds.left, bounds.right);
    const clampedY = clamp(data.y, bounds.top, bounds.bottom);

    // Create new state with final position
    let newSlideState = { ...slideStateRef.current };
    const updatePosition = (items) => items.map(item => 
        item.id === id ? { ...item, x: clampedX, y: clampedY } : item
    );

    if (type === 'textbox') {
        newSlideState.textboxes = updatePosition(newSlideState.textboxes);
    } else if (type === 'image') {
        newSlideState.images = updatePosition(newSlideState.images);
    } else if (type === 'iframe') {
        newSlideState.iframes = updatePosition(newSlideState.iframes);
    }

    // Cancel any pending RAF from drag
    if (dragRafRef.current) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
    }

    // Record final position in history
    recordHistory(newSlideState);
  }, [recordHistory]);

  // Add event listener for keydown to handle enter key for auto-continue and tab for indentation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && activeElement?.type === 'textbox') {
        e.preventDefault();
        const newTextboxes = slideStateRef.current.textboxes.map(box => {
          if (box.id === activeElement.id) {
            const lines = box.text.split('\n');
            const selectionStart = textareaRefs.current[box.id]?.selectionStart || box.text.length;
            const currentLineIndex = box.text.slice(0, selectionStart).split('\n').length - 1;
            const currentLine = lines[currentLineIndex];
            const isList = currentLine.startsWith('• ');
            const isNumberedList = /^\d+\.\s/.test(currentLine);
            if (isList || isNumberedList) {
              const newLine = isList ? '• ' : `${lines.length + 1}. `;
              lines.splice(currentLineIndex + 1, 0, newLine);
            } else {
              lines.splice(currentLineIndex + 1, 0, '');
            }
            return { ...box, text: lines.join('\n') };
          }
          return box;
        });
        setSlideState({ ...slideStateRef.current, textboxes: newTextboxes });
      } else if (e.key === 'Tab' && activeElement?.type === 'textbox') {
        e.preventDefault();
        const newTextboxes = slideStateRef.current.textboxes.map(box => {
          if (box.id === activeElement.id) {
            const lines = box.text.split('\n');
            const selectionStart = textareaRefs.current[box.id]?.selectionStart || box.text.length;
            const currentLineIndex = box.text.slice(0, selectionStart).split('\n').length - 1;
            const currentLine = lines[currentLineIndex];
            const isList = currentLine.startsWith('• ');
            const isNumberedList = /^\d+\.\s/.test(currentLine);
            if (isList || isNumberedList) {
              const newLine = isList ? '◦ ' : `  ${String.fromCharCode(97 + currentLineIndex)}. `;
              lines[currentLineIndex] = newLine + currentLine.trim().replace(/^•\s*|^\d+\.\s*/, '');
              return { ...box, text: lines.join('\n') };
            }
          }
          return box;
        });
        setSlideState({ ...slideStateRef.current, textboxes: newTextboxes });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeElement]);

  // Update handleStyleChange to toggle list formatting
  const handleStyleChange = useCallback((property, value) => {
    if (activeElement?.type !== 'textbox') return;

    const newTextboxes = slideStateRef.current.textboxes.map(box => {
      if (box.id === activeElement.id) {
        if (property === 'isList' || property === 'isNumberedList') {
          const lines = box.text.split('\n');
          const updatedText = value ? lines.map((line, index) => {
            if (property === 'isList') {
              return `• ${line.replace(/^\d+\.\s*/, '')}`;
            } else {
              return `${index + 1}. ${line.replace(/^•\s*/, '')}`;
            }
          }).join('\n') : lines.map(line => line.replace(/^•\s*|^\d+\.\s*/, '')).join('\n');
          return { ...box, text: updatedText, isList: property === 'isList' ? value : false, isNumberedList: property === 'isNumberedList' ? value : false };
        }
        return { ...box, [property]: value, height: 'auto' };
      }
      return box;
    });
    recordHistory({ ...slideStateRef.current, textboxes: newTextboxes });
  }, [activeElement, recordHistory]);

  const handleBackgroundChange = useCallback((newBackground) => {
      if (newBackground === '__upload_image__') {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;
          APIUploadAsset(
            file,
            'deck_image',
            ({ finalUrl }) => {
              if (!finalUrl) return;
              const bg = { type: 'image', url: finalUrl };
              recordHistory({ ...slideStateRef.current, background: bg });
            },
            (err) => {
              alert('Failed to upload background image');
            }
          );
        };
        document.body.appendChild(input);
        input.click();
        // cleanup later
        setTimeout(() => { try { document.body.removeChild(input); } catch (e) {} }, 0);
        return;
      }
      recordHistory({ ...slideStateRef.current, background: newBackground });
  }, [recordHistory]);
  
  const handleDelete = useCallback(() => {
      if (!activeElement) return;
      
      let newSlideState = { ...slideStateRef.current };
      if (activeElement.type === 'textbox') {
          const newTextboxes = slideStateRef.current.textboxes.filter(box => box.id !== activeElement.id);
          newSlideState = { ...newSlideState, textboxes: newTextboxes };
      } else if (activeElement.type === 'image') {
          const newImages = slideStateRef.current.images.filter(img => img.id !== activeElement.id);
          newSlideState = { ...newSlideState, images: newImages };
      } else if (activeElement.type === 'iframe') {
          const newIframes = slideStateRef.current.iframes.filter(ifr => ifr.id !== activeElement.id);
          newSlideState = { ...newSlideState, iframes: newIframes };
      }

      recordHistory(newSlideState);
      setActiveElement(null);
  }, [activeElement, recordHistory]);

  useEffect(() => {
    const handleKeyDown = (e) => {
        if (activeElement && (e.key === 'Delete' || e.key === 'Backspace')) {
        // If the key event originated inside TipTap (ProseMirror) or an input, ignore
            const inProseMirrorByTarget = e.target?.closest?.('.ProseMirror');
            const sel = window.getSelection && window.getSelection();
            const inProseMirrorBySelection = sel?.anchorNode && sel.anchorNode.parentElement?.closest?.('.ProseMirror');
            const ae = document.activeElement;
            const isInputFocused = (
              ae?.tagName === 'INPUT' ||
              ae?.tagName === 'TEXTAREA' ||
              ae?.classList?.contains('ProseMirror') ||
              ae?.closest?.('.ProseMirror')
            );


            if (inProseMirrorByTarget || inProseMirrorBySelection || isInputFocused) {
              // Stop bubbling so no other global handlers (e.g., slide delete) see this key
              e.stopPropagation();
              e.stopImmediatePropagation();
              return; // allow default so the editor performs its deletion
            }

            // Otherwise, treat as delete current element on canvas
            isHandlingDelete.current = true;
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            handleDelete();
            setTimeout(() => { isHandlingDelete.current = false; }, 100);
            return false;
        }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [activeElement, handleDelete]);

  // Update button click handlers
  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    // Mark that we're handling a delete
    isHandlingDelete.current = true;
    
    // Handle the delete
    handleDelete();
    
    // Reset the flag after a short delay
    setTimeout(() => {
      isHandlingDelete.current = false;
    }, 100);
    
    return false;
  };

  // Update toolbar render functions to use new click handler
  const renderTextToolbar = () => {
    if (!activeTextbox) {
      return null;
    }

    return (
      <div className="text-toolbar">
        <h5 className="mb-3">Text Properties</h5>
        <Form.Group className="mb-3">
          <Form.Label className="small">Font Size</Form.Label>
          <Form.Control
            size="sm"
            type="text"
            value={fontSizeInput}
            onFocus={e => {
              fontSizeInputFocused.current = true;
            }}
            onChange={e => {
                const value = e.target.value;
                // Allow only digits or empty string
                if (value === '' || /^\d+$/.test(value)) {
                  setFontSizeInput(value);
                }
            }}
            onBlur={e => {
              fontSizeInputFocused.current = false;
              const value = e.target.value;
              if (value === '' || parseInt(value, 10) < 1) {
                handleStyleChange('fontSize', 24); // Default font size
                setFontSizeInput('24');
              } else {
                const numValue = parseInt(value, 10);
                const finalValue = Math.min(Math.max(numValue, 1), 500);
                handleStyleChange('fontSize', finalValue);
                setFontSizeInput(String(finalValue));
              }
            }}
            onKeyDown={e => {
              // Apply changes on Enter
              if (e.key === 'Enter') {
                e.target.blur();
              }
            }}
            style={{ width: '70px' }}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Link URL</Form.Label>
          <Form.Control
            size="sm"
            type="text"
            placeholder="https://example.com"
            value={activeTextbox.linkUrl || ''}
            onChange={(e) => handleStyleChange('linkUrl', e.target.value)}
          />
          <Form.Text className="text-muted">Leave blank for no link</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Font Color</Form.Label>
          <Form.Control type="color" value={activeTextbox.fontColor} onChange={e => handleStyleChange('fontColor', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Font Family</Form.Label>
          <Form.Select size="sm" value={activeTextbox.fontFamily} onChange={e => handleStyleChange('fontFamily', e.target.value)}>
            {FONT_FAMILIES.map(font => <option key={font} value={font}>{font}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group className="d-flex justify-content-start gap-2">
          <Button size="sm" variant={activeTextbox.isBold ? "primary" : "outline-primary"} onClick={() => handleStyleChange('isBold', !activeTextbox.isBold)} style={{width: '40px'}}><b>B</b></Button>
          <Button size="sm" variant={activeTextbox.isItalic ? "primary" : "outline-primary"} onClick={() => handleStyleChange('isItalic', !activeTextbox.isItalic)} style={{width: '40px'}}><i>I</i></Button>
          <Button size="sm" variant={activeTextbox.isUnderline ? "primary" : "outline-primary"} onClick={() => handleStyleChange('isUnderline', !activeTextbox.isUnderline)} style={{width: '40px'}}><u>U</u></Button>
          <Button 
            variant={activeTextbox.isList ? 'primary' : 'outline-secondary'}
            onClick={() => {
              const ed = window.__tiptapEditors?.get(activeTextbox.id);
              ed && ed.chain().focus().toggleBulletList().run();
            }}
          >
            <FontAwesomeIcon icon={faListUl} />
          </Button>
          <Button 
            variant={activeTextbox.isNumberedList ? 'primary' : 'outline-secondary'}
            onClick={() => {
              const ed = window.__tiptapEditors?.get(activeTextbox.id);
              ed && ed.chain().focus().toggleOrderedList().run();
            }}
          >
            <FontAwesomeIcon icon={faListOl} />
          </Button>
        </Form.Group>
        <hr />
        <Button 
          variant="outline-danger" 
          size="sm" 
          onClick={handleDeleteClick}
        >
          <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
          Delete Textbox
        </Button>
      </div>
    );
  };

  const renderImageToolbar = () => {
    return (
      <div className="image-toolbar">
        <h5 className="mb-3">Image Properties</h5>
        <Button 
          variant="outline-danger" 
          size="sm" 
          onClick={handleDeleteClick}
        >
          <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
          Delete Image
        </Button>
      </div>
    );
  };

  // Update the iframe toolbar to show in the left sidebar
  const renderIframeToolbar = () => {
    const iframe = slideState.iframes.find(f => f.id === activeElement?.id);
    if (!iframe) {
      return null;
    }

    const isSnorkleVisual = iframe.src?.includes('vis.snorkle');
    const iframeSettings = slideState.iframeSettings || {};
    const showQR = isSnorkleVisual && iframeSettings[iframe.id]?.showQR;


    return (
      <>
        <h6>Actions</h6>
        {isSnorkleVisual && (
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id={`qr-switch-${iframe.id}`}
              label="Show QR Code"
              checked={showQR || false}
              onChange={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                const newSettings = {
                  ...(slideState.iframeSettings || {}),
                  [iframe.id]: {
                    ...(slideState.iframeSettings?.[iframe.id] || {}),
                    showQR: e.target.checked
                  }
                };
                
                const newState = {
                  ...slideState,
                  iframeSettings: newSettings
                };

                
                onSlideStateChange(newState);
              }}
            />
            <Form.Text className="text-muted">
              QR code will appear in the bottom-right corner of this visual
            </Form.Text>
          </Form.Group>
        )}
        <Button 
          variant="outline-danger"
          size="sm"
          className="w-100 mb-2"
          onClick={() => handleDelete()}
        >
          <FontAwesomeIcon icon={faTrash} className="me-2" />
          Delete Visual
        </Button>
      </>
    );
  };

  const renderSlideToolbar = () => {
    return (
      <div className="toolbar-section">
        <h6>Slide Settings</h6>
        
        {/* Background color */}
        <Form.Group className="mb-3">
          <Form.Label className="small">Background</Form.Label>
          <Form.Select
            size="sm"
            value={slideState.background}
            onChange={(e) => {
              const newState = {
                ...slideState,
                background: e.target.value
              };
              onSlideStateChange(newState);
            }}
          >
            {BACKGROUND_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Popup settings */}
        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            id="popup-switch"
            label="Show Popup Message"
            checked={slideState.showPopup}
            onChange={(e) => {
              if (e.target.checked) {
                // Open dialog to add popup
                setTempPopupTitle(slideState.popupTitle || '');
                setTempPopupText(slideState.popupText || '');
                setShowPopupDialog(true);
              } else {
                // Clear popup
                const newState = {
                  ...slideState,
                  showPopup: false,
                  popupTitle: '',
                  popupText: ''
                };
                onSlideStateChange(newState);
              }
            }}
          />
          {slideState.showPopup && (
            <Button
              variant="link"
              size="sm"
              className="p-0 mt-1"
              onClick={() => {
                setTempPopupTitle(slideState.popupTitle || '');
                setTempPopupText(slideState.popupText || '');
                setShowPopupDialog(true);
              }}
            >
              Edit Popup Message
            </Button>
          )}
        </Form.Group>
      </div>
    );
  };

  // Helper to get QR URL from visual cache - shared between components
  const getQrUrl = useCallback((src) => {
    if (!src?.includes('vis.snorkle')) return null;
    
    // Extract visual ID from URL - try multiple patterns
    let id = null;
    
    // Try different URL patterns
    const patterns = [
      // Pattern 1: Traditional .html pattern
      /vis\.snorkle\.io\/([^/?#]+)\.html/,
      // Pattern 2: viewer.html pattern
      /vis\.snorkle\.io\/([^/?#]+)\/viewer\.html/,
      // Pattern 3: Any path segment between domain and file
      /vis\.snorkle\.io\/([^/?#.]+)/,
      // Pattern 4: id parameter in query string
      /vis\.snorkle\.io.*[?&]id=([^&]+)/
    ];
    
    for (const pattern of patterns) {
      const match = src.match(pattern);
      if (match) {
        // Clean up the ID - remove any trailing /
        id = match[1].replace(/\/$/, '');
        break;
      }
    }
    
    if (!id) {
      return null;
    }
    
    const visual = visCache?.[id];
    const qrUrl = visual?.qr_image || visual?.qr_url;
    
    
    return qrUrl;
  }, [visCache]);

  // Update generateSlideHtml to use the shared QR URL function
  const localGenerateSlideHtml = useCallback(() => {
    return generateSlideHtml(slideState, visCache);
  }, [slideState, visCache]);

  const handleDownload = () => {
    const htmlContent = localGenerateSlideHtml();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'custom-slide.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePreview = () => {
    const htmlContent = localGenerateSlideHtml();
    const previewWindow = window.open();
    previewWindow.document.write(htmlContent);
    previewWindow.document.close();
  };

  const handleSaveAndAdd = () => {
    onSave(localGenerateSlideHtml());
  };

  const activeTextbox = slideState.textboxes.find(box => activeElement?.id === box.id && activeElement?.type === 'textbox');

  // Sync font size input with active textbox (only when not actively editing)
  useEffect(() => {
    if (activeTextbox && activeTextbox.fontSize && !fontSizeInputFocused.current) {
      setFontSizeInput(String(activeTextbox.fontSize));
    }
  }, [activeTextbox?.id, activeTextbox?.fontSize]);

  // Add export JSON button
  const renderExportButtons = () => {
    return (
      <div className="export-buttons">
        <Button variant="primary" onClick={handlePreview} className="me-2">
          <FontAwesomeIcon icon={faEye} className="me-2" />
          Preview Slide
        </Button>
        <Button variant="success" onClick={handleDownload} className="me-2">
          <FontAwesomeIcon icon={faFileAlt} className="me-2" />
          Save as HTML
        </Button>
      </div>
    );
  };

  // Expose getCurrentState method to parent
  useImperativeHandle(ref, () => ({
    getCurrentState: () => slideState
  }));

  // Update setActiveElement to notify parent of iframe selection
  const handleElementSelect = (element) => {

    setActiveElement(element);
    if (element?.type === 'iframe') {
      const iframe = slideState.iframes.find(f => f.id === element.id);
      onIframeSelect?.(iframe);
    } else if (onIframeSelect) {
      onIframeSelect(null);
    }
  };

  // Update handleCanvasClick to be more explicit about deselection
  const handleCanvasClick = (e) => {
    if (!isSelecting) {
      setActiveElement(null);
      setSelectedElements([]);
      onIframeSelect?.(null);
    }
  };

  // Add these state variables near the top where other useState hooks are
  const [showPopupDialog, setShowPopupDialog] = useState(false);
  const [tempPopupTitle, setTempPopupTitle] = useState('');
  const [tempPopupText, setTempPopupText] = useState('');

  // Add this component near other modal/dialog components
  const PopupDialog = ({ show, onHide, initialTitle = '', initialText = '', initialFont = 'Arial', onSave }) => {
    const [title, setTitle] = useState(initialTitle);
    const [text, setText] = useState(initialText);
    const [font, setFont] = useState(initialFont);

    // Update local state when initialTitle/initialText change
    useEffect(() => {
      setTitle(initialTitle);
      setText(initialText);
    }, [initialTitle, initialText]);

    const handleSave = () => {
      onSave(title, text, font);
      onHide();
    };

    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
          <Modal.Title>{initialTitle ? 'Edit Popup Message' : 'Add Popup Message'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
        <Form.Group className="mb-3">
              <Form.Label>Title (optional)</Form.Label>
          <Form.Control 
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter popup title"
          />
        </Form.Group>
        <Form.Group className="mb-3">
              <Form.Label>Font</Form.Label>
              <Form.Select
                value={font}
                onChange={(e) => setFont(e.target.value)}
              >
                {FONT_FAMILIES.map(font => (
                  <option key={font} value={font}>{font}</option>
                ))}
          </Form.Select>
        </Form.Group>
            <Form.Group>
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter popup message"
              />
        </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #dee2e6' }}>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>Save</Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className={`text-editor-container ${embedded ? 'embedded' : ''}`} style={{ pointerEvents: isThumbnail ? 'none' : 'auto' }}>
      {embedded ? (
        // PowerPoint-like layout for embedded mode
        <>
          {/* Ribbon Toolbar */}
          <div className="ribbon-toolbar">
            {/* File group */}
            <div className="ribbon-group">
              <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Preview current slide</Tooltip>}
            >
              <Button size="sm" variant="outline-secondary" onClick={handlePreview} title="Preview current slide">
                <FontAwesomeIcon icon={faEye} />
              </Button>
              </OverlayTrigger>
            </div>
            {/* Insert group (label removed) */}
            <div className="ribbon-group">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Add Textbox</Tooltip>}
            >
              <Button size="sm" className="add-button insert-btn" style={{ padding: '6px 10px', fontSize: '14px' }} onClick={handleAddTextbox} title="Text box">
                <FontAwesomeIcon icon={faFont} className="insert-icon" />
              </Button>
              </OverlayTrigger>
              <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Add Image</Tooltip>}
            >
              <Button size="sm" className="add-button insert-btn" style={{ padding: '6px 10px', fontSize: '14px' }} onClick={openImageFilePicker} title="Image">
                <FontAwesomeIcon icon={faImage} className="insert-icon" />
              </Button>
              </OverlayTrigger>
              <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Add Snorkle Visual</Tooltip>}
            >
              <Button size="sm" className="add-button insert-btn snorkle-btn" style={{ padding: '6px 10px', fontSize: '14px' }} onClick={() => setShowVisualPicker(true)} title="Snorkle visual">
                <img src="/snorkle_vis_thumbnails/atom2.png" alt="Snorkle Visual" className="insert-icon-img" />
              </Button>
              </OverlayTrigger>
              <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Add Popup Message</Tooltip>}
            >
              <Button size="sm" className="add-button insert-btn" style={{ padding: '6px 10px', fontSize: '14px' }} onClick={() => {
                setTempPopupTitle(slideState.popupTitle || '');
                setTempPopupText(slideState.popupText || '');
                setShowPopupDialog(true);
              }} title="Popup message">
                <FontAwesomeIcon icon={faCommentDots} className="insert-icon" />
              </Button>
              </OverlayTrigger>
            </div>

            {/* Background group */}
            <div className="ribbon-group">
              <div className="ribbon-title">Background</div>
              <Form.Select
                size="sm"
                value={slideState.background}
                onChange={(e) => handleBackgroundChange(e.target.value)}
                style={{ width: 160 }}
              >
                {BACKGROUND_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.name}</option>
                ))}
              </Form.Select>
            </div>

            {/* Context group: Text */}
            {activeElement?.type === 'textbox' && activeTextbox && (
                <>
                                <div className="ribbon-group">
                  <div className="ribbon-title">Text</div>
                  <Form.Select 
                    size="sm" 
                    style={{ width: '120px' }}
                    value={activeTextbox.fontFamily || 'Arial'}
                    onChange={(e) => handleStyleChange('fontFamily', e.target.value)}
                  >
                    <option value="Arial">Arial</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Verdana">Verdana</option>
                  </Form.Select>
                  </div>
                  <div className="ribbon-group">
                  <Form.Control 
                    type="text" 
                    size="sm"
                    style={{ width: '60px' }}
                    value={fontSizeInput}
                    onFocus={(e) => {
                      fontSizeInputFocused.current = true;
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only digits or empty string
                      if (value === '' || /^\d+$/.test(value)) {
                        setFontSizeInput(value);
                      }
                    }}
                    onBlur={(e) => {
                      fontSizeInputFocused.current = false;
                      const value = e.target.value;
                      if (value === '' || parseInt(value, 10) < 1) {
                        handleStyleChange('fontSize', 24);
                        setFontSizeInput('24');
                      } else {
                        const numValue = parseInt(value, 10);
                        const finalValue = Math.min(Math.max(numValue, 1), 500);
                        handleStyleChange('fontSize', finalValue);
                        setFontSizeInput(String(finalValue));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                  />
                  <ButtonGroup size="sm">
                    <Button 
                      variant={activeTextbox.isBold ? 'primary' : 'outline-secondary'}
                      onClick={() => handleStyleChange('isBold', !activeTextbox.isBold)}
                    >
                      <FontAwesomeIcon icon={faBold} />
                    </Button>
                    <Button 
                      variant={activeTextbox.isItalic ? 'primary' : 'outline-secondary'}
                      onClick={() => handleStyleChange('isItalic', !activeTextbox.isItalic)}
                    >
                      <FontAwesomeIcon icon={faItalic} />
                    </Button>
                    <Button 
                      variant={activeTextbox.isUnderline ? 'primary' : 'outline-secondary'}
                      onClick={() => handleStyleChange('isUnderline', !activeTextbox.isUnderline)}
                    >
                      <FontAwesomeIcon icon={faUnderline} />
                    </Button>
                    <Button 
                      variant={activeTextbox.linkUrl ? 'primary' : 'outline-secondary'}
                      title={activeTextbox.linkUrl ? activeTextbox.linkUrl : 'Add link'}
                      onClick={() => {
                        const url = window.prompt('Enter link URL (leave blank to remove):', activeTextbox.linkUrl || '');
                        if (url !== null) {
                          handleStyleChange('linkUrl', url.trim());
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faLink} />
                    </Button>
                    <Button 
                      variant={activeTextbox.isList ? 'primary' : 'outline-secondary'}
                      onClick={() => {
                        const ed = window.__tiptapEditors?.get(activeTextbox.id);
                        ed && ed.chain().focus().toggleBulletList().run();
                      }}
                    >
                      <FontAwesomeIcon icon={faListUl} />
                    </Button>
                    <Button 
                      variant={activeTextbox.isNumberedList ? 'primary' : 'outline-secondary'}
                      onClick={() => {
                        const ed = window.__tiptapEditors?.get(activeTextbox.id);
                        ed && ed.chain().focus().toggleOrderedList().run();
                      }}
                    >
                      <FontAwesomeIcon icon={faListOl} />
                    </Button>
                  </ButtonGroup>
                  </div>
                  <div className="ribbon-group">
                  <div className="color-picker-wrapper">
                    <div 
                      className="color-picker-button"
                      id="color-picker-button"
                      style={{ backgroundColor: activeTextbox.fontColor || '#000000' }}
                      onClick={() => setShowColorPicker(!showColorPicker)}
                    />  
                    {showColorPicker && (
                      <div className="color-picker-dropdown" style={getColorPickerPositioningStyle()}>
                        <SketchPicker 
                          color={activeTextbox.fontColor || '#000000'}
                          onChange={(color) => handleStyleChange('fontColor', color.hex)}
                        />
                      </div>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline-danger"
                    onClick={() => handleDelete()}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
                </>
            )}
            {activeElement?.type === 'image' && (
              <div className="ribbon-group">
                <Button 
                  size="sm" 
                  variant="outline-danger"
                  onClick={() => handleDelete()}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </div>
            )}
            {activeElement?.type === 'iframe' && (() => {
              const iframe = slideState.iframes.find(f => f.id === activeElement.id);
              const isSnorkleVisual = iframe?.src?.includes('vis.snorkle');
              const showQR = isSnorkleVisual ? (slideState.iframeSettings?.[iframe.id]?.showQR ?? false) : false;
              return (
                <div className="ribbon-group">
                  {isSnorkleVisual && (
                    <Form.Check
                      type="switch"
                      id={`qr-switch-${iframe.id}`}
                      label="Show QR"
                      checked={showQR}
                      onChange={(e) => {
                        const newSettings = {
                          ...(slideState.iframeSettings || {}),
                          [iframe.id]: {
                            ...(slideState.iframeSettings?.[iframe.id] || {}),
                            showQR: e.target.checked
                          }
                        };
                        const newState = { ...slideState, iframeSettings: newSettings };
                        onSlideStateChange(newState);
                      }}
                    />
                  )}
                  <Button 
                    size="sm" 
                    variant="outline-danger"
                    onClick={() => handleDelete()}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              );
            })()}
            {/* removed copy/paste buttons from ribbon */}
          </div>

          {/* Main Content Area */}
          <div className="editor-main-content">
            {/* Left Sidebar - Add Tools */}
            <div className="editor-left-sidebar">
              <h6>Insert</h6>
              <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Add Textbox</Tooltip>}
            >
              <Button onClick={handleAddTextbox} size="sm" className="add-button">
                <FontAwesomeIcon icon={faFont} className="me-2" />
                Text Box
              </Button>
              </OverlayTrigger>
              <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Add Image</Tooltip>}
            >
              <Button onClick={openImageFilePicker} size="sm" className="add-button">
                <FontAwesomeIcon icon={faImage} className="me-2" />
                Image
              </Button>
              </OverlayTrigger>
              <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Add Snorkle Visual</Tooltip>}
            >
              <Button onClick={() => setShowVisualPicker(true)} size="sm" className="add-button snorkle-btn">
                <FontAwesomeIcon icon={faAtom} className="me-2" />
                Snorkle Visual
              </Button>
              </OverlayTrigger>
              <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tip-change-thumb">Add Popup Message</Tooltip>}
            >
              <Button
                variant={slideState.showPopup ? "primary" : "outline-primary"}
                size="sm"
                className="add-button"
                onClick={() => {
                  setTempPopupTitle(slideState.popupTitle || '');
                  setTempPopupText(slideState.popupText || '');
                  setShowPopupDialog(true);
                }}
              >
                <FontAwesomeIcon icon={faCommentDots} className="me-2" />
                {slideState.showPopup ? 'Edit Popup Message' : 'Add Popup Message'}
                </Button>
              </OverlayTrigger>
              <hr className="my-3" />
              
              <h6>Actions</h6>
              {/* Add QR toggle for Snorkle visuals */}
              {activeElement?.type === 'iframe' && (() => {
                const iframe = slideState.iframes.find(f => f.id === activeElement.id);
                if (!iframe) return null;
                
                const isSnorkleVisual = iframe.src?.includes('vis.snorkle');
                if (!isSnorkleVisual) return null;

                const iframeSettings = slideState.iframeSettings || {};
                const showQR = iframeSettings[iframe.id]?.showQR;

                return (
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id={`qr-switch-${iframe.id}`}
                      label="Show QR Code"
                      checked={showQR || false}
                      onChange={(e) => {
                        e.stopPropagation(); // Prevent event bubbling
                        const newSettings = {
                          ...(slideState.iframeSettings || {}),
                          [iframe.id]: {
                            ...(slideState.iframeSettings?.[iframe.id] || {}),
                            showQR: e.target.checked
                          }
                        };
                        
                        const newState = {
                          ...slideState,
                          iframeSettings: newSettings
                        };

                        
                        onSlideStateChange(newState);
                      }}
                    />
                    <Form.Text className="text-muted">
                      QR code will appear in the bottom-right corner of this visual
                    </Form.Text>
                  </Form.Group>
                );
              })()}
              <Button onClick={handleCopy} size="sm" className="add-button" variant="outline-secondary" disabled={!activeElement && selectedElements.length === 0}>
                <FontAwesomeIcon icon={faCopy} className="me-2" />
                Copy
              </Button>
              <Button onClick={handlePaste} size="sm" className="add-button" variant="outline-secondary" disabled={copiedElements.length === 0}>
                <FontAwesomeIcon icon={faPaste} className="me-2" />
                Paste
              </Button>
              {renderExportButtons()}
              <Button variant="secondary" onClick={onCancel}>Back to Options</Button>
            </div>

            {/* Canvas Area */}
            <div className="editor-canvas-area">
              <div className="canvas-wrapper">
                <div className="aspect-ratio-container">
                  <div 
                    className="slide-canvas"
                    ref={slideCanvasRef}
                    style={
                      (typeof slideState.background === 'object' && slideState.background?.type === 'image')
                        ? {
                            backgroundImage: `url(${slideState.background.url})`,
                            backgroundPosition: 'center',
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat'
                          }
                        : { background: slideState.background }
                    }
                    onClick={(e) => {
                      handleCanvasClick(e);
                    }}
                    onMouseDown={(e) => {
                      // Allow selection on slide-canvas or scaling-container (empty areas)
                      const isCanvasArea = e.target === e.currentTarget || 
                                         e.target.classList.contains('scaling-container');
                      
                      if (isCanvasArea) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const startPoint = {
                          x: (e.clientX - rect.left) / scale,
                          y: (e.clientY - rect.top) / scale
                        };
                        setSelectionStart(startPoint);
                        setIsSelecting(true);
                        handleElementSelect(null);
                      }
                    }}
                    onMouseMove={(e) => {
                      if (isSelecting && selectionStart) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const endPoint = {
                          x: (e.clientX - rect.left) / scale,
                          y: (e.clientY - rect.top) / scale
                        };
                        setSelectionEnd(endPoint);
                      }
                    }}
                    onMouseUp={() => {
                      if (isSelecting) {
                        const newSelectedElements = getElementsInSelection(selectionStart, selectionEnd);
                        setSelectedElements(newSelectedElements);
                        setIsSelecting(false);
                        setSelectionStart(null);
                        setSelectionEnd(null);
                      }
                    }}
                  >
                    <div 
                      className="scaling-container"
                      style={{ transform: `scale(${scale})` }}
                    >
                      {/* Selection box */}
                      {isSelecting && selectionStart && (
                        <>
                          <div
                            className="selection-box"
                            style={{
                              left: Math.min(selectionStart.x, selectionEnd?.x || selectionStart.x),
                              top: Math.min(selectionStart.y, selectionEnd?.y || selectionStart.y),
                              width: Math.abs((selectionEnd?.x || selectionStart.x) - selectionStart.x),
                              height: Math.abs((selectionEnd?.y || selectionStart.y) - selectionStart.y),
                            }}
                          />
                        </>
                      )}
                      
                      {[
                        ...slideState.images.map(b => ({...b, __type:'image'})),
                        ...slideState.iframes.map(b => ({...b, __type:'iframe'})),
                        ...slideState.textboxes.map(b => ({...b, __type:'textbox'})),
                      ]
                        .sort((a,b) => (a.stackOrder || 0) - (b.stackOrder || 0))
                        .map((el) => (
                          el.__type === 'image' ? (
                            <EditableImage
                              key={`image-${el.id}`}
                              box={el}
                              scale={scale}
                              isActive={activeElement?.id === el.id && activeElement?.type === 'image'}
                              isSelected={isElementSelected(el.id, 'image')}
                              onDragStop={onDragStop}
                              onResizeStart={onResizeStart}
                              onResizeStop={onResizeStop}
                              onSetActive={handleElementSelect}
                            />
                          ) : el.__type === 'iframe' ? (
                            <EditableIframe
                              key={`iframe-${el.id}`}
                              box={el}
                              scale={scale}
                              isActive={activeElement?.id === el.id && activeElement?.type === 'iframe'}
                              isSelected={selectedElements.some(s => s.id === el.id && s.type === 'iframe')}
                              onDragStop={onDragStop}
                              onResizeStart={onResizeStart}
                              onResizeStop={onResizeStop}
                              onSetActive={handleElementSelect}
                              slideState={slideState}
                            />
                          ) : (
                            <EditableTextbox
                              key={`textbox-${el.id}`}
                              box={el}
                              scale={scale}
                              isActive={activeElement?.id === el.id && activeElement?.type === 'textbox'}
                              isSelected={isElementSelected(el.id, 'textbox')}
                              onDragStop={onDragStop}
                              onResizeStart={onResizeStart}
                              onResizeStop={onResizeStop}
                              onSetActive={handleElementSelect}
                              onTextChange={handleTextChange}
                              textareaRef={textareaRefs}
                              onHtmlChange={(id, html) => {
                                const newTextboxes = slideStateRef.current.textboxes.map(b =>
                                  b.id === id ? { ...b, html } : b
                                );
                                const newSlideState = { ...slideStateRef.current, textboxes: newTextboxes };
                                recordHistory(newSlideState);
                              }}
                              onEditorReady={(id, editor) => {
                                if (!window.__tiptapEditors) window.__tiptapEditors = new Map();
                                window.__tiptapEditors.set(id, editor);
                              }}
                            />
                          )
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Toolbar - Background Options */}
          <div className="editor-bottom-toolbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h6 className="mb-0">Slide Background</h6>
              <div className="background-palette">
                {BACKGROUND_OPTIONS.map((bg, index) => (
                  <div
                    key={index}
                    className={`background-option ${slideState.background === bg.value ? 'active' : ''}`}
                    style={{ background: bg.value }}
                    onClick={() => handleBackgroundChange(bg.value)}
                    title={bg.name}
                  />
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        // Original layout for standalone mode
        <>
          <div className="editor-main-area">
            <div className="canvas-wrapper">
              <div className="aspect-ratio-container">
                <div className="slide-canvas" ref={slideCanvasRef} onClick={() => handleElementSelect(null)}>
                  <div className="scaling-container" style={{ transform: `scale(${scale})`, background: slideState.background }}>
                    {[
                      ...slideState.images.map(b => ({...b, __type:'image'})),
                      ...slideState.iframes.map(b => ({...b, __type:'iframe'})),
                      ...slideState.textboxes.map(b => ({...b, __type:'textbox'})),
                    ]
                      .sort((a,b) => (a.stackOrder || 0) - (b.stackOrder || 0))
                      .map((el) => (
                        el.__type === 'image' ? (
                          <EditableImage
                            key={`image-${el.id}`}
                            box={el}
                            scale={scale}
                            isActive={activeElement?.id === el.id && activeElement?.type === 'image'}
                            onDragStop={onDragStop}
                            onResizeStart={onResizeStart}
                            onResizeStop={onResizeStop}
                            onSetActive={handleElementSelect}
                          />
                        ) : el.__type === 'iframe' ? (
                          <EditableIframe
                            key={`iframe-${el.id}`}
                            box={el}
                            scale={scale}
                            isActive={activeElement?.id === el.id && activeElement?.type === 'iframe'}
                            isSelected={selectedElements.some(s => s.id === el.id && s.type === 'iframe')}
                            onDragStop={onDragStop}
                            onResizeStart={onResizeStart}
                            onResizeStop={onResizeStop}
                            onSetActive={handleElementSelect}
                            slideState={slideState}
                          />
                        ) : (
                          <EditableTextbox
                            key={`textbox-${el.id}`}
                            box={el}
                            scale={scale}
                            isActive={activeElement?.id === el.id && activeElement?.type === 'textbox'}
                            onDragStop={onDragStop}
                            onResizeStart={onResizeStart}
                            onTextChange={handleTextChange}
                            onSetActive={handleElementSelect}
                            textareaRef={textareaRefs}
                            onHtmlChange={(id, html) => {
                              const newTextboxes = slideStateRef.current.textboxes.map(b =>
                                b.id === id ? { ...b, html } : b
                              );
                              const newSlideState = { ...slideStateRef.current, textboxes: newTextboxes };
                              recordHistory(newSlideState);
                            }}
                            onEditorReady={(id, editor) => {
                              if (!window.__tiptapEditors) window.__tiptapEditors = new Map();
                              window.__tiptapEditors.set(id, editor);
                            }}
                          />
                        )
                      ))}
                  </div>
                </div>
              </div>
            </div>
            {!embedded && (
              <div className="editor-controls">
                <Button onClick={handleAddTextbox}>Add Textbox</Button>
                <Button onClick={openImageFilePicker}>
                  <FontAwesomeIcon icon={faImage} className="me-2" />
                  Add Image
                </Button>
                <Button onClick={() => onOpenVisualPicker ? onOpenVisualPicker() : setShowVisualPicker(true)}>
                  <FontAwesomeIcon icon={faAtom} className="me-2" />
                  Add Snorkle Visual
                </Button>
                {renderExportButtons()}
                <Button variant="secondary" onClick={onCancel}>Back to Options</Button>
              </div>
            )}
          </div>
          {embedded && (
            <div className="editor-sidebar">
              <div className="mb-3">
                <Button onClick={handleAddTextbox} size="sm" className="w-100 mb-2">
                  <FontAwesomeIcon icon={faFont} className="me-2" />
                  Text Box
                </Button>
                <Button onClick={openImageFilePicker} size="sm" className="w-100 mb-2">
                  <FontAwesomeIcon icon={faImage} className="me-2" />
                  Image
                </Button>
                <Button onClick={() => setShowVisualPicker(true)} size="sm" className="w-100 mb-2">
                  <FontAwesomeIcon icon={faAtom} className="me-2" />
                  Snorkle Visual
                </Button>
                <Button
                  variant={slideState.showPopup ? "primary" : "outline-primary"}
                  size="sm"
                  className="w-100 mb-2"
                  onClick={() => {
                    setTempPopupTitle(slideState.popupTitle || '');
                    setTempPopupText(slideState.popupText || '');
                    setShowPopupDialog(true);
                  }}
                >
                  <FontAwesomeIcon icon={faCommentDots} className="me-2" />
                  {slideState.showPopup ? 'Edit Popup Message' : 'Add Popup Message'}
                </Button>
              </div>

              <hr className="my-3" />
              <h6>Settings</h6>
              <Form.Select
                size="sm"
                className="mb-2"
                value={slideState.background}
                onChange={(e) => {
                  const newState = {
                    ...slideState,
                    background: e.target.value
                  };
                  onSlideStateChange(newState);
                }}
              >
                {BACKGROUND_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>

              <hr className="my-3" />

              <h6>Actions</h6>
              {/* Add QR toggle for Snorkle visuals */}
              {activeElement?.type === 'iframe' && (() => {
                const iframe = slideState.iframes.find(f => f.id === activeElement.id);
                if (!iframe) return null;
                
                const isSnorkleVisual = iframe.src?.includes('vis.snorkle');
                if (!isSnorkleVisual) return null;

                const iframeSettings = slideState.iframeSettings || {};
                const showQR = iframeSettings[iframe.id]?.showQR;

                return (
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id={`qr-switch-${iframe.id}`}
                      label="Show QR Code"
                      checked={showQR || false}
                      onChange={(e) => {
                        e.stopPropagation(); // Prevent event bubbling
                        const newSettings = {
                          ...(slideState.iframeSettings || {}),
                          [iframe.id]: {
                            ...(slideState.iframeSettings?.[iframe.id] || {}),
                            showQR: e.target.checked
                          }
                        };
                        
                        const newState = {
                          ...slideState,
                          iframeSettings: newSettings
                        };

                        
                        onSlideStateChange(newState);
                      }}
                    />
                    <Form.Text className="text-muted">
                      QR code will appear in the bottom-right corner of this visual
                    </Form.Text>
                  </Form.Group>
                );
              })()}
              <Button onClick={handleCopy} size="sm" className="add-button" variant="outline-secondary" disabled={!activeElement && selectedElements.length === 0}>
                <FontAwesomeIcon icon={faCopy} className="me-2" />
                Copy
              </Button>
              <Button onClick={handlePaste} size="sm" className="add-button" variant="outline-secondary" disabled={copiedElements.length === 0}>
                <FontAwesomeIcon icon={faPaste} className="me-2" />
                Paste
              </Button>
              {renderExportButtons()}
              <Button variant="secondary" onClick={onCancel}>Back to Options</Button>
            </div>
          )}
          {!embedded && (
            <div className="editor-sidebar">
              {activeElement?.type === 'textbox' && renderTextToolbar()}
              {activeElement?.type === 'image' && renderImageToolbar()}
              {activeElement?.type === 'iframe' && (
                <div>
                  {renderIframeToolbar()}
                </div>
              )}
              {!activeElement && renderSlideToolbar()}
            </div>
          )}
        </>
      )}
      {showVisualPicker && (
          <VisualPickerModal 
            show={showVisualPicker}
            onHide={() => setShowVisualPicker(false)}
            onSelect={handleVisualSelected}
            userInfo={userInfo}
            visCache={visCache}
            owner={"self"}
            defaultView="options"
            lightTheme={true}
          />
      )}
      <PopupDialog
        show={showPopupDialog}
        onHide={() => {
          setShowPopupDialog(false);
        }}
        initialTitle={slideState.popupTitle || ''}
        initialText={slideState.popupText || ''}
        initialFont={slideState.popupFont || 'Arial'}
        onSave={(title, text, font) => {
          const newState = {
            ...slideState,
            showPopup: true,
            popupTitle: title,
            popupText: text,
            popupFont: font
          };
          onSlideStateChange(newState);
          setShowPopupDialog(false);
        }}
      />
    </div>
  );
});

export default CustomTextSlideEditor; 